import { TOURNAMENT_ORIGIN, OPT_IN_STATUS, HISTORY_STATUS_VARIANT } from '@constants/tournamentConstants'

const pad = (n) => String(n).padStart(2, '0')

// Splits a live countdown's remaining ms into the 4 digital-clock boxes
// defenderV2 shows (Days:Hours:Mins:Secs) — always all 4, zero-padded, never
// collapsed (unlike the generic `useCountdown` hook's "2D 06H" label).
export const splitCountdown = (remainingMs) => {
  const totalSec = Math.max(0, Math.floor((remainingMs ?? 0) / 1000))
  return {
    days: pad(Math.floor(totalSec / 86400)),
    hours: pad(Math.floor((totalSec % 86400) / 3600)),
    minutes: pad(Math.floor((totalSec % 3600) / 60)),
    seconds: pad(totalSec % 60),
  }
}

// Converts a raw "HH:mm:ss" UTC time-of-day to the viewer's local "hh:mm AM/PM"
// — ports defenderV2's utcTimeToLocal exactly (build today's UTC instant at
// that time, then read it back with local getters).
export const utcTimeToLocal = (utcTime) => {
  if (!utcTime) return ''
  const [h, m, s = '0'] = utcTime.split(':')
  const now = new Date()
  const utcDate = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), Number(h), Number(m), Number(s))
  )
  let hours = utcDate.getHours()
  const minutes = utcDate.getMinutes()
  const period = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12 || 12
  return `${pad(hours)}:${pad(minutes)} ${period}`
}

const parseTimeToMinutes = (time) => {
  const [clock, period] = time.trim().split(' ')
  let [hour, minute] = clock.split(':').map(Number)
  if (period) {
    const isPM = period.toUpperCase() === 'PM'
    if (hour === 12) hour = isPM ? 12 : 0
    else if (isPM) hour += 12
  }
  return hour * 60 + minute
}

// Wraparound-duration full-day check (ports defenderV2's isFullDay) — NOT a
// naive string compare against "00:00:00"/"23:59:59", since the window is
// evaluated in LOCAL time after the UTC->local conversion above.
const isFullDaySchedule = (fromLocal, toLocal) => {
  const duration = (parseTimeToMinutes(toLocal) - parseTimeToMinutes(fromLocal) + 1440) % 1440
  return duration === 0 || duration >= 1439
}

// "Full Day" vs "hh:mm AM - hh:mm PM" schedule label, in the viewer's local time.
export const deriveScheduleLabel = (timeFrom, timeTo) => {
  if (!timeFrom || !timeTo) return ''
  const fromLocal = utcTimeToLocal(timeFrom)
  const toLocal = utcTimeToLocal(timeTo)
  return isFullDaySchedule(fromLocal, toLocal) ? 'Full Day' : `${fromLocal} - ${toLocal}`
}

// Pure derivation of a tournament row -> everything the card shows. The ONE
// place status/badge/footer is derived, mirroring defenderV2's
// TOURNAMENT_UI_CONTROLLER + PRIMARY_BUTTON_CONFIG + getTournamentCardStatus,
// but from ONE canonical source per field (optInStatus/canOptIn/rank), not a
// raw status string read from a *different* field depending on which card
// instance renders the same tournament (defenderV2's list card trusted
// `userOptin`; its detail card trusted `status` — for the same row).
//
// `isDetail` = the card is on the TournamentDetail screen. List and detail
// show different footers for the SAME tournament (list = a single "View
// Tournament"/"Tournament Result" button + a status badge; detail = the real
// Accept/Decline controls, or a decided-state pill, and no badge).
export const deriveTournamentCardView = (tournament, origin, { isDetail = false } = {}) => {
  if (!tournament) return null

  if (origin === TOURNAMENT_ORIGIN.HISTORY) {
    const hasRank = tournament.rank != null
    const legacy = HISTORY_STATUS_VARIANT[String(tournament.status).toUpperCase()]

    return {
      statusBadge: legacy ?? HISTORY_STATUS_VARIANT.EXPIRED,
      showCountdown: false,
      showSchedule: false,
      showRankings: true,
      hasRank,
      // Ports getTournamentCardStatus's 'expired' branch exactly: the detail
      // pill is decided purely by whether the player has a rank at all, not
      // by any opt-in field (history tournaments are already over).
      footer: isDetail
        ? hasRank
          ? { type: 'PILL', label: 'Accepted', variant: 'accepted' }
          : { type: 'PILL', label: 'Declined', variant: 'declined' }
        : { type: 'BUTTON', label: 'Tournament Result' },
    }
  }

  const { optInStatus, canOptIn } = tournament
  const isAccepted = optInStatus === OPT_IN_STATUS.ACCEPTED
  const isDeclined = optInStatus === OPT_IN_STATUS.DECLINED
  const optInRequired = Boolean(canOptIn)

  const footer = !isDetail
    ? { type: 'BUTTON', label: 'View Tournament' }
    : isAccepted
      ? { type: 'PILL', label: 'Accepted', variant: 'accepted' }
      : isDeclined
        ? { type: 'PILL', label: 'Declined', variant: 'declined' }
        : optInRequired
          ? { type: 'ACCEPT_DECLINE' }
          : { type: 'PILL', label: 'Default Accepted', variant: 'default' }

  return {
    statusBadge: isDetail
      ? null
      : !optInRequired
        ? { label: 'Default', variant: 'default' }
        : isAccepted
          ? { label: 'Accepted', variant: 'accepted' }
          : isDeclined
            ? { label: 'Declined', variant: 'declined' }
            : { label: 'Active', variant: 'active' },
    showCountdown: true,
    showSchedule: true,
    showRankings: false,
    footer,
  }
}
